"use client";

import { Button } from "@/components/ui/primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/primitives/dialog";
import { Switch } from "@/components/ui/primitives/switch";
import { COOKIE_POLICY_URL } from "@/lib/constants/legal";
import type { CookieConsent } from "@/lib/utils/cookie-consent";
import { Lock, Shield } from "lucide-react";
import Link from "next/link";
import { useCallback, useEffect, useState } from "react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CategoryDescriptor {
  id: keyof Pick<CookieConsent, "necessary" | "functional" | "analytics">;
  label: string;
  description: string;
  locked: boolean;
}

const CATEGORIES: CategoryDescriptor[] = [
  {
    id: "necessary",
    label: "Strictly Necessary",
    description:
      "Required for authentication, session management, and core platform functionality. These cannot be disabled.",
    locked: true,
  },
  {
    id: "functional",
    label: "Functional",
    description:
      "Remembers your UI preferences such as sidebar state, table column settings, and dashboard view options. Without these, preferences reset each session.",
    locked: false,
  },
  {
    id: "analytics",
    label: "Analytics & Performance",
    description:
      "Helps us understand how you use the platform and monitor performance (page views, web vitals, error tracking). Powered by PostHog. No data is shared with third-party advertisers.",
    locked: false,
  },
];

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface CookiePreferencesModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly initialConsent: CookieConsent | null;
  readonly onSave: (
    choices: Pick<CookieConsent, "functional" | "analytics">,
  ) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CookiePreferencesModal({
  open,
  onOpenChange,
  initialConsent,
  onSave,
}: CookiePreferencesModalProps) {
  const [functional, setFunctional] = useState(true);
  const [analytics, setAnalytics] = useState(true);

  // Sync toggles with the current consent whenever the modal opens
  useEffect(() => {
    if (open && initialConsent) {
      setFunctional(initialConsent.functional);
      setAnalytics(initialConsent.analytics);
    } else if (open) {
      setFunctional(true);
      setAnalytics(true);
    }
  }, [open, initialConsent]);

  const handleSave = useCallback(() => {
    onSave({ functional, analytics });
    onOpenChange(false);
  }, [functional, analytics, onSave, onOpenChange]);

  const toggleFor = (id: CategoryDescriptor["id"]) => {
    if (id === "necessary") return true;
    if (id === "functional") return functional;
    return analytics;
  };

  const setterFor = (id: CategoryDescriptor["id"]) => {
    if (id === "functional") return setFunctional;
    if (id === "analytics") return setAnalytics;
    return undefined;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md" aria-describedby="cookie-prefs-desc">
        {/* Header */}
        <div className="shrink-0 flex flex-col gap-1.5 border-b border-border px-6 py-4">
          <DialogTitle className="flex items-center gap-2 text-base font-semibold">
            <Shield className="h-4 w-4 text-text-secondary" />
            Cookie Preferences
          </DialogTitle>
          <DialogDescription
            id="cookie-prefs-desc"
            className="text-sm text-text-secondary"
          >
            Choose which cookies you allow. You can change these settings at any
            time from the{" "}
            <Link
              href={COOKIE_POLICY_URL}
              className="underline underline-offset-2 hover:text-text-primary transition-colors"
            >
              Cookie Policy
            </Link>{" "}
            page.
          </DialogDescription>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-5">
          {CATEGORIES.map((cat) => (
            <div
              key={cat.id}
              className="flex items-start justify-between gap-4"
            >
              <div className="space-y-0.5 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-medium text-text-primary">
                    {cat.label}
                  </span>
                  {cat.locked && <Lock className="h-3 w-3 text-text-muted" />}
                </div>
                <p className="text-xs text-text-secondary leading-relaxed">
                  {cat.description}
                </p>
              </div>
              <Switch
                checked={toggleFor(cat.id)}
                onCheckedChange={setterFor(cat.id)}
                disabled={cat.locked}
                aria-label={`${cat.label} cookies`}
              />
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="shrink-0 border-t border-border bg-muted/50 px-6 py-4 flex justify-end gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button variant="default" size="sm" onClick={handleSave}>
            Save Preferences
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
